import React from 'react';
import { Link } from 'react-router-dom';
import {
  LuMoon,
  LuSun,
  LuCheck,
  LuShieldCheck,
  LuTrash2,
  LuPencilLine,
  LuLayers,
  LuCircleHelp,
  LuChevronRight,
} from 'react-icons/lu';
import XiPageShell from '../components/xi/XiPageShell';
import useTheme from '../hooks/useTheme';
import useTranslation from '../hooks/useTranslation';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ur', label: 'Urdu' },
  { code: 'zh', label: 'Chinese (Mandarin)' },
  { code: 'it', label: 'Italian' },
  { code: 'ko', label: 'Korean' },
];

function ThemeOption({ active, onClick, icon: Icon, label, hint }) {
  return (
    <button
      type="button"
      className={`xi-theme-option${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span className="xi-theme-option__icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="xi-theme-option__text">
        <span className="xi-theme-option__label">{label}</span>
        <span className="xi-theme-option__hint">{hint}</span>
      </span>
      {active && <LuCheck className="xi-theme-option__check" aria-hidden="true" />}
    </button>
  );
}

/**
 * Settings — only real, wired controls.
 *
 * Theme writes through the shared ThemeProvider (localStorage.theme_mode +
 * body[data-theme]). Language writes through the existing TranslationProvider
 * (localStorage.selected_language_v1). There are no decorative toggles.
 */
export default function Settings() {
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useTranslation();

  return (
    <XiPageShell
      eyebrow="Preferences"
      title="Settings"
      lede="Appearance, language and account controls. Changes apply immediately across every Xi page."
    >
      <section className="xi-section" style={{ marginTop: 0 }}>
        <h2 className="xi-section__title">Appearance</h2>
        <div className="xi-card xi-settings-card" style={{ padding: 'clamp(16px, 2.4vw, 24px)' }}>
          <div className="xi-theme-grid">
            <ThemeOption
              active={theme === 'dark'}
              onClick={() => setTheme('dark')}
              icon={LuMoon}
              label="Midnight"
              hint="Deep navy surfaces, violet illumination, cyan accents."
            />
            <ThemeOption
              active={theme === 'light'}
              onClick={() => setTheme('light')}
              icon={LuSun}
              label="Daylight"
              hint="Lavender-white surfaces, indigo type, soft elevation."
            />
          </div>
          <p className="xi-row__hint" style={{ marginTop: 14 }}>
            Your choice is saved on this device and applied before the first paint, so
            there is no flash when you open Xi again.
          </p>
        </div>
      </section>

      <section className="xi-section">
        <h2 className="xi-section__title">Language</h2>
        <div className="xi-card xi-settings-card">
          <div className="xi-row">
            <div className="xi-row__text">
              <label className="xi-row__label" htmlFor="xi-language">
                Interface language
              </label>
              <span className="xi-row__hint">
                Translates Xi&apos;s interface text. Voice conversations use the language
                you speak.
              </span>
            </div>
            <select
              id="xi-language"
              className="xi-select"
              style={{ maxWidth: 240 }}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="xi-section">
        <h2 className="xi-section__title">Account</h2>
        <div className="xi-grid-2">
          <Link to="/profile/edit" className="xi-link-card">
            <span className="xi-link-card__icon" aria-hidden="true">
              <LuPencilLine />
            </span>
            <span className="xi-link-card__text">
              <span className="xi-link-card__title">Edit profile</span>
              <span className="xi-link-card__sub">Name, age, phone, job title and language</span>
            </span>
            <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
          </Link>

          <Link to="/reset-password" className="xi-link-card">
            <span className="xi-link-card__icon" aria-hidden="true">
              <LuShieldCheck />
            </span>
            <span className="xi-link-card__text">
              <span className="xi-link-card__title">Reset password</span>
              <span className="xi-link-card__sub">Change the password for this account</span>
            </span>
            <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
          </Link>

          <Link to="/plans" className="xi-link-card">
            <span className="xi-link-card__icon" aria-hidden="true">
              <LuLayers />
            </span>
            <span className="xi-link-card__text">
              <span className="xi-link-card__title">Subscription</span>
              <span className="xi-link-card__sub">Compare plans and manage billing</span>
            </span>
            <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
          </Link>

          <Link to="/help" className="xi-link-card">
            <span className="xi-link-card__icon" aria-hidden="true">
              <LuCircleHelp />
            </span>
            <span className="xi-link-card__text">
              <span className="xi-link-card__title">Help &amp; support</span>
              <span className="xi-link-card__sub">Common questions and contact</span>
            </span>
            <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="xi-section">
        <h2 className="xi-section__title">Danger zone</h2>
        <div className="xi-card xi-settings-card">
          <div className="xi-row">
            <div className="xi-row__text">
              <span className="xi-row__label">Deactivate account</span>
              <span className="xi-row__hint">
                Permanently delete your account and stored reports. This cannot be undone.
              </span>
            </div>
            <Link
              to="/delete"
              className="xi-btn xi-btn--danger"
              style={{ height: 42, fontSize: '0.86rem' }}
            >
              <LuTrash2 aria-hidden="true" />
              Deactivate
            </Link>
          </div>
        </div>
      </section>
    </XiPageShell>
  );
}
